import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogoLockup } from '../components/LogoLockup';
import { api, extractApiError } from '../api/client';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      const apiErr = extractApiError(err);
      // Always show success to prevent email enumeration
      if (apiErr.code === 'USER_NOT_FOUND') {
        setSent(true);
      } else {
        setError(apiErr.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050510', padding: '1rem' }}>
      <div style={{ width: '100%', maxWidth: '24rem' }}>
        <div className="flex flex-col items-center mb-8 gap-3">
          <LogoLockup size="lg" />
        </div>
        <div className="card">
          {sent ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📧</div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#f0f0ff', marginBottom: 8 }}>Check your email</h2>
              <p style={{ color: '#8888aa', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
                If an account exists for <strong style={{ color: '#f0f0ff' }}>{email}</strong>, we've sent a password reset link.
              </p>
              <button onClick={() => navigate('/auth')} className="btn-primary w-full">
                Back to Sign In
              </button>
            </div>
          ) : (
            <>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#f0f0ff', marginBottom: 8 }}>Reset your password</h2>
              <p style={{ color: '#8888aa', fontSize: 14, marginBottom: 20 }}>
                Enter your email and we'll send you a reset link.
              </p>
              {error && (
                <div className="mb-4 bg-red-900/30 border border-red-700 rounded-lg px-3 py-2 text-red-400 text-sm">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="label">Email address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="input-field"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : 'Send Reset Link'}
                </button>
              </form>
              <div className="mt-4 pt-4 text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <button type="button" onClick={() => navigate('/auth')} className="text-xs text-brand-400 hover:text-brand-300 font-medium">
                  Back to Sign In
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
